import { State } from '@progress/kendo-data-query';
import {
  Grid,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import {
  FC,
  ReactElement,
  cloneElement,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useViewReceiptColumns } from 'src/assets/static/view_receipt/viewReceiptColumns';
import GridWrapper from 'src/components/GridWrapper';
import Layout from 'src/components/Layout';
import Navbar from 'src/components/NavBar';
import ScansPreviewSection from 'src/components/PreviewSection';
import SelectCompany from 'src/components/SelectCompany';
import Button from 'src/components/design-system/Button';
import useLinkControl from 'src/components/useLinkControl';
import { api } from 'src/constants/apiRoutes';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useRedirect from 'src/hooks/useRedirect';
import useSplitter from 'src/hooks/useSplitter';
import useWindowSize from 'src/hooks/useWindowSize';
import ActionButtons from 'src/sections/view_receipts/ActionButtons';
import GridToolbar, { additionalFiltersArray } from 'src/sections/view_receipts/GridToolBar';
import { IFilters } from 'src/types/PostBody';
import { IReceiptListItem } from 'src/types/Receipt';
import axios from 'src/utils/axios';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatViewReceipts } from 'src/utils/transformation';

type ViewReceiptsState = State & { skipCalculatingTotalRecordsCount: boolean };

const initialReceiptDataState: ViewReceiptsState = {
  skip: 0,
  take: 20,
  skipCalculatingTotalRecordsCount: false,
  sort: [
    {
      field: 'receiptStatusDate',
      dir: 'desc',
    },
  ],
  filter: {
    logic: 'and',
    filters: [],
  },
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const RECEIPT_STATUS_FIELD = 'receiptStatusDate';
const PO_DATE_FIELD = 'poDate';
const RECEIPT_DATE_FIELD = 'receiptDate';
const dateArr = [RECEIPT_STATUS_FIELD, PO_DATE_FIELD, RECEIPT_DATE_FIELD];

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  excelFileName: 'View Receipt.xlsx',
  tableTextId: 'Receipts',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const receiptSelectionFilterInitialState = {
  value: 0,
  label: 'Show All Active Receipts',
};

export interface ViewInvoiceListResponse {
  items: IReceiptListItem[];
  totalRecords: number;
}

export interface Option {
  value: number;
  label: string;
}

interface ViewReceiptsProps {}

const ViewReceipts: FC<ViewReceiptsProps> = () => {
  const { getUrl, redirect } = useRedirect();
  const { height } = useWindowSize();
  const { company: selectedCompany } = useCompany();
  const [searchParams] = useSearchParams();
  const { value: refresh, toggle: toggleRefresh } = useBoolean();
  const poNumber = useMemo(() => searchParams.get('poNumber') || '', [searchParams]);
  const tableRef = createRef<Grid>();
  const { LinkControl, openNewTab } = useLinkControl('ViewReceipt');

  const [receiptDataState, setReceiptDataState] = useState<State>(initialReceiptDataState);
  const [receiptSelectionFilter, setReceiptSelectionFilter] = useState<Option>(
    receiptSelectionFilterInitialState
  );

  const [rows, setRows] = useState<IReceiptListItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const togglePreview = useCallback(() => setShowPreview((prev) => !prev), []);
  const viewReceiptColumns = useViewReceiptColumns(tableRef, rows);
  const previewDocument = useMemo(() => rows.find((row) => row.preview), [rows]);

  const listCompanyReceipts = useCallback(
    (companyId: string, includeReceiptDetails: boolean = false, clientSide: boolean = false) => {
      const data = { ...modifyRequest(receiptDataState, dateArr), clientSide };

      if (poNumber) {
        const filterByPo = { field: 'poNum', value: poNumber, operator: 'eq' };
        data.filters.push(filterByPo);
      }

      if (receiptSelectionFilter.value >= 0) {
        data.filters = [
          ...data.filters,
          ...(additionalFiltersArray[receiptSelectionFilter.value] as IFilters[]),
        ];
      }

      return axios.post<ViewInvoiceListResponse>(
        api.viewReceiptsUrlFn(companyId, includeReceiptDetails),
        data
      );
    },
    [poNumber, receiptDataState, receiptSelectionFilter.value]
  );

  useEffect(() => {
    setLoading(true);
    if (selectedCompany.companyId) {
      listCompanyReceipts(selectedCompany.companyId, false, false)
        .then((res) => {
          const { items = [], totalRecords = 0 } = res?.data;
          setRows((prev) => formatViewReceipts(items, selectedCompany?.companyName, prev));
          setCount(totalRecords || 0);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [listCompanyReceipts, refresh, selectedCompany.companyId, selectedCompany.companyName]);

  const previewAttachments = useMemo(
    () => (
      <ScansPreviewSection
        poStatus={previewDocument?.poStatus as string}
        receiptId={previewDocument?.receiptId as number}
        status={previewDocument?.receiptStatus as string}
        isInvoice={false}
        companyId={parseInt(selectedCompany?.companyId)}
        totalDocCount={previewDocument?.numOfAttachments}
        refresh={toggleRefresh}
      />
    ),
    [
      previewDocument?.numOfAttachments,
      previewDocument?.poStatus,
      previewDocument?.receiptId,
      previewDocument?.receiptStatus,
      selectedCompany?.companyId,
      toggleRefresh,
    ]
  );

  const onAttachmentClick = useCallback((dataItem: IReceiptListItem) => {
    setShowPreview(true);
    setRows((prev) =>
      [...prev].map((row) => ({
        ...row,
        ...(row?.receiptId === dataItem?.receiptId ? { preview: true } : { preview: false }),
      }))
    );
  }, []);

  const rowRender = useCallback(
    (trElement: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
      const invoiceId = props?.dataItem?.receiptId;
      let trProps: any = { ...trElement.props };
      if (previewDocument && invoiceId === previewDocument?.receiptId) {
        trProps = {
          ...trProps,
          className: `${trElement.props.className} ${props.className} ${
            props.dataItem.preview ? 'border-preview-box' : ''
          }`,
        };
      }
      return cloneElement(trElement, { ...trProps });
    },
    [previewDocument]
  );

  const onCreateNewClick = useCallback(
    (dataItem: any) => {
      redirect(
        getUrl('enterReceipt')(dataItem.poNum, selectedCompany.companyId),
        false,
        openNewTab
      );
    },
    [getUrl, openNewTab, redirect, selectedCompany.companyId]
  );

  const onReceiptDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setReceiptDataState(e.dataState),
    []
  );

  const handleClearFilter = useCallback(() => {
    setReceiptSelectionFilter(receiptSelectionFilterInitialState);
    setReceiptDataState(initialReceiptDataState);
    setShowPreview(false);
  }, []);

  const onReceiptSelect = useCallback((event: GridSelectionChangeEvent) => {
    setRows((prev) => {
      const prevItems = [...prev];
      const index = event?.startRowIndex;
      prevItems[index].selected = !prevItems[index]?.selected;

      return prevItems;
    });
  }, []);

  const onHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement: any = event.syntheticEvent.target;
    const { checked } = checkboxElement;
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  }, []);

  const selectedRows = useMemo(() => rows?.filter((row) => row?.selected), [rows]);

  const cellClickFns = useCallback(
    () => ({
      onCreateNewClick,
      onAttachmentClick,
      openNewTab,
    }),
    [onCreateNewClick, onAttachmentClick, openNewTab]
  );

  const exportColumns = useMemo(
    () =>
      viewReceiptColumns
        .concat([{ field: 'companyName', title: 'Company Name' }])
        .filter((c) => c.field !== 'selected' && c.field !== 'numOfAttachments'),
    [viewReceiptColumns]
  );

  const isClearFiltersDisabled = useMemo(
    () => !receiptDataState.filter?.filters?.length,
    [receiptDataState.filter?.filters?.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <GridToolbar
        setGridLoader={setLoading}
        clearFilter={handleClearFilter}
        selectionFilter={receiptSelectionFilter}
        setSelectionFilter={setReceiptSelectionFilter}
        columns={exportColumns}
        listCurrentPageReceipts={listCompanyReceipts}
        setReceiptDataState={setReceiptDataState}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [
      exportColumns,
      handleClearFilter,
      isClearFiltersDisabled,
      listCompanyReceipts,
      receiptSelectionFilter,
    ]
  );

  const table = useMemo(
    () => (
      <GridWrapper
        ref={tableRef}
        columns={viewReceiptColumns}
        dataSource={rows}
        dataState={receiptDataState}
        total={count}
        height={`${height - 98}px`}
        onDataStateChange={onReceiptDataStateChange}
        onSelectionChange={onReceiptSelect}
        onHeaderSelectionChange={onHeaderSelectionChange}
        gridToolbarChildren={gridToolbarChildren}
        cellClick={cellClickFns}
        loading={loading}
        rowRender={rowRender}
        {...defaultProps}
      />
    ),
    [
      cellClickFns,
      count,
      gridToolbarChildren,
      height,
      loading,
      onHeaderSelectionChange,
      onReceiptDataStateChange,
      onReceiptSelect,
      receiptDataState,
      rowRender,
      rows,
      tableRef,
      viewReceiptColumns,
    ]
  );

  const page = useSplitter(table, previewAttachments, showPreview);

  const handleCompanyChange = useCallback(() => {
    setReceiptDataState(initialReceiptDataState);
  }, []);

  return (
    <div>
      <Navbar title="View Receipts" />
      <Layout>
        <div className="flex justify-between flex-wrap">
          <div className="flex">
            <SelectCompany className="items-center" onChange={handleCompanyChange} />
          </div>
          {selectedCompany?.companyId && (
            <>
              <div className="flex flex-wrap">
                <ActionButtons
                  selectedRows={selectedRows}
                  setRows={setRows}
                  refresh={toggleRefresh}
                />
              </div>
              <div className="flex items-center">
                <div className="mr-2">
                  <LinkControl />
                </div>
                <Button color="light" onClick={togglePreview}>
                  {showPreview ? 'Hide Scans' : 'Show Scans'}
                </Button>
              </div>
            </>
          )}
        </div>
        {page}
      </Layout>
    </div>
  );
};

export default ViewReceipts;
